import React, { useEffect, useState } from 'react';
import {
  fetchCompleteResult,
  getSpeakingCompleteResult,
  getComprehensionCompleteResult,
  getLogicCompleteResult,
} from '../services/api';
import { TESTS, TestKey, toTestKey } from '../data/tests';

/* ------------------------------------------------------------------ *
 * Each activity has its own result endpoint with its own shape, so
 * every group carries its own column headings and pre-built cells.
 * ------------------------------------------------------------------ */

interface ReviewRow {
  /** Optional label rendered as a spanning sub-header above the row (used for story titles). */
  section?: string;
  cells: React.ReactNode[];
  /** `null` means the item was never attempted. */
  correct: boolean | null;
}

interface ReviewGroup {
  key: string;
  label: string;
  head: string[];
  rows: ReviewRow[];
  /** Set when the activity was completed but the endpoint returned no per-question detail. */
  notice?: string;
  error?: string;
}

const text = (value: any, fallback = '—'): string => {
  const out = value === 0 ? '0' : String(value ?? '').trim();
  return out || fallback;
};

const optionLabel = (value: any): string =>
  typeof value === 'number' && Number.isFinite(value) ? `Option ${value + 1}` : '';

/** The child's answer, set in a chip like the old results table. */
const Answer: React.FC<{ children: React.ReactNode; right?: boolean }> = ({ children, right }) => (
  <span className={right ? 'sl-ans sl-ans--right' : 'sl-ans'}>{children}</span>
);

/* ---------- per-activity extraction ---------- */

const spellingGroup = (data: any): ReviewGroup => {
  const results = (Array.isArray(data?.results) && data.results.length
    ? data.results
    : data?.teacher_admin_detail?.table_data) || [];
  return {
    key: 'spelling',
    label: TESTS.spelling.title,
    head: ['Word', 'Child spelled', 'Result'],
    rows: (Array.isArray(results) ? results : []).map((r: any) => {
      const word = r.word || r.target_word || '';
      const attempt = r.user_input ?? r.attempt ?? r.answer ?? '';
      const notAnswered = r.icon === 'Not answered';
      return {
        cells: [text(word), <Answer>{text(attempt, 'Not answered')}</Answer>],
        correct: notAnswered
          ? null
          : r.result === 'Correct' || r.is_correct === true || r.correct === true,
      };
    }),
  };
};

const speakingGroup = (data: any): ReviewGroup => {
  // Every activity carries its teacher table under
  // `teacher_admin_detail.table_data`; the old `all_results`/`results`
  // shapes no longer exist on this endpoint.
  const results =
    data?.teacher_admin_detail?.table_data || data?.all_results || data?.results || [];
  return {
    key: 'speaking',
    label: TESTS.speaking.title,
    head: ['Sentence', 'What we heard', 'Score', 'Result'],
    rows: (Array.isArray(results) ? results : []).map((r: any) => {
      const attempted = r.status === 'Answered';
      const score = r.overall_score ?? r.overall?.score ?? r.score;
      return {
        // The pill already says "Not attempted", so the cells just stay empty.
        cells: [
          text(r.original_sentence ?? r.sentence),
          <Answer>{text(r.said ?? r.transcribed_text)}</Answer>,
          attempted && score != null ? `${score}%` : '—',
        ],
        correct: attempted ? (score ?? 0) >= 85 : null,
      };
    }),
  };
};

const readingGroup = (data: any): ReviewGroup => {
  const stories = data?.story_breakdown || data?.results || [];
  const rows: ReviewRow[] = [];
  (Array.isArray(stories) ? stories : []).forEach((story: any) => {
    const title = story.story_title || story.title || '';
    (story.questions || []).forEach((q: any, i: number) => {
      rows.push({
        section: i === 0 && title ? title : undefined,
        cells: [
          text(q.question),
          <Answer>{text(q.selected_answer ?? optionLabel(q.selected_index), 'Not answered')}</Answer>,
          <Answer right>{text(q.correct_answer ?? optionLabel(q.correct_index))}</Answer>,
        ],
        correct: q.answered === false ? null : q.is_correct ?? null,
      });
    });
  });
  return {
    key: 'reading',
    label: TESTS.reading.title,
    head: ['Question', "Child's answer", 'Correct answer', 'Result'],
    rows,
  };
};

const logicGroup = (data: any): ReviewGroup => {
  // The teacher table is the per-item breakdown this endpoint carries; the
  // older aliases below are kept for any payload that still uses them.
  const results =
    data?.teacher_admin_detail?.table_data ||
    data?.responses ||
    data?.item_responses ||
    data?.detailed_responses ||
    data?.question_breakdown ||
    data?.item_breakdown ||
    data?.results ||
    data?.items ||
    [];
  const list: any[] = Array.isArray(results) ? results : [];
  // The old results table showed how long each puzzle took; keep it when the endpoint sends it.
  const hasTime = list.some((r: any) => (r.response_time_seconds ?? r.time) != null);
  const rows: ReviewRow[] = list.map((r: any) => {
    const seconds = r.response_time_seconds ?? r.time;
    // An item the child never reached is "Not attempted", not a wrong answer.
    const notAnswered = r.icon === 'Not answered' || r.error_type === 'Not answered';
    return {
      cells: [
        text(r.question_text ?? r.question ?? r.item_number ?? r.item_id),
        <Answer>{text(r.selected_answer ?? r.selected_answer_text ?? optionLabel(r.selected_answer_index), 'Not answered')}</Answer>,
        <Answer right>{text(r.correct_answer ?? r.correct_answer_text ?? optionLabel(r.correct_answer_index))}</Answer>,
        ...(hasTime ? [seconds != null ? `${seconds}s` : '—'] : []),
      ],
      correct: notAnswered ? null : r.is_correct ?? r.correct ?? null,
    };
  });
  const total = data?.summary?.total_items ?? data?.total_items;
  return {
    key: 'logic',
    label: TESTS.logic.title,
    head: ['Question', "Child's answer", 'Correct answer', ...(hasTime ? ['Time'] : []), 'Result'],
    rows,
    notice:
      !rows.length && total
        ? `${total} puzzles were completed, but this activity did not return a question-by-question breakdown.`
        : undefined,
  };
};

/* ---------- fetch + assemble ---------- */

const loadGroups = async (childId: string, grade: string): Promise<ReviewGroup[]> => {
  const settled = await Promise.allSettled([
    fetchCompleteResult(childId, grade),
    getSpeakingCompleteResult(childId, grade),
    getComprehensionCompleteResult(childId, grade),
    getLogicCompleteResult(childId, undefined, grade),
  ]);

  return settled.map((outcome, i) => {
    if (outcome.status === 'rejected') {
      return {
        key: KEYS[i],
        label: LABELS[i],
        head: [],
        rows: [],
        error: 'This activity has not been completed yet.',
      };
    }
    return buildGroup(i, outcome.value);
  });
};

const KEYS = ['spelling', 'speaking', 'reading', 'logic'];
const LABELS = [TESTS.spelling.title, TESTS.speaking.title, TESTS.reading.title, TESTS.logic.title];
const BUILDERS = [spellingGroup, speakingGroup, readingGroup, logicGroup];
/** The admin preview returns the same payloads keyed `comprehension`, not `reading`. */
const PREFETCH_KEYS = ['spelling', 'speaking', 'comprehension', 'logic'];

const buildGroup = (i: number, data: any): ReviewGroup => {
  // Endpoints can answer 200 with `success: false` when there is nothing stored.
  if (data?.success === false) {
    return { key: KEYS[i], label: LABELS[i], head: [], rows: [], error: 'This activity has not been completed yet.' };
  }
  return BUILDERS[i](data);
};

const buildPrefetched = (data: Record<string, any>): ReviewGroup[] =>
  PREFETCH_KEYS.map((key, i) =>
    buildGroup(i, data?.[key] ?? { success: false })
  );

/* ---------- presentation ---------- */

const ResultPill: React.FC<{ correct: boolean | null }> = ({ correct }) => {
  if (correct === null) return <span className="sl-pill sl-pill--skip">Not attempted</span>;
  return correct ? (
    <span className="sl-pill sl-pill--yes">Correct</span>
  ) : (
    <span className="sl-pill sl-pill--no">Not yet</span>
  );
};

/** The journey card's artwork, so each table is labelled the way the test was. */
const GroupIcon: React.FC<{ groupKey: string }> = ({ groupKey }) => {
  const key = toTestKey(groupKey) as TestKey | undefined;
  if (!key) return null;
  const identity = TESTS[key];
  return (
    <span className="sl-rev__icon">
      <img
        src={identity.logo}
        alt=""
        onError={(e) => {
          e.currentTarget.style.display = 'none';
          const fallback = e.currentTarget.nextElementSibling as HTMLElement | null;
          if (fallback) fallback.style.display = 'inline';
        }}
      />
      <span style={{ display: 'none' }} aria-hidden="true">
        {identity.icon}
      </span>
    </span>
  );
};

const Group: React.FC<{ group: ReviewGroup; forceOpen?: boolean }> = ({ group, forceOpen }) => {
  const [open, setOpen] = useState(false);
  const expanded = open || forceOpen;
  const total = group.rows.length;
  const correct = group.rows.filter((r) => r.correct === true).length;
  const unavailable = !total;

  return (
    <div className="sl-rev__group">
      <button
        type="button"
        className="sl-rev__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={expanded}
        disabled={unavailable}
      >
        <GroupIcon groupKey={group.key} />
        <span className="sl-rev__name">{group.label}</span>
        <span className="sl-rev__score">
          {unavailable ? group.error || 'No detail available' : `${correct} of ${total} correct`}
        </span>
        {!unavailable && <span className="sl-rev__chevron" data-open={open} aria-hidden="true" />}
      </button>

      {group.notice && <p className="sl-rev__notice">{group.notice}</p>}

      {expanded && !unavailable && (
        <div className="sl-rev__frame">
          <div className="sl-rev__scroll">
            <table className="sl-rev__table">
              <thead>
                <tr>
                  {group.head.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {group.rows.map((row, i) => (
                  <React.Fragment key={i}>
                    {row.section && (
                      <tr className="sl-rev__section">
                        <td colSpan={group.head.length}>{row.section}</td>
                      </tr>
                    )}
                    <tr data-correct={row.correct === null ? 'skip' : row.correct}>
                      {row.cells.map((cell, j) => (
                        <td key={j}>{cell}</td>
                      ))}
                      <td>
                        <ResultPill correct={row.correct} />
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

interface AnswerReviewProps {
  childId: string;
  grade: string;
  childName?: string;
  /** Render every table open - used when the page is being captured as a PDF. */
  expandAll?: boolean;
  /** Results already fetched elsewhere (the admin preview); skips the calls. */
  prefetched?: Record<string, any>;
}

const AnswerReview: React.FC<AnswerReviewProps> = ({ childId, grade, childName, expandAll, prefetched }) => {
  const [groups, setGroups] = useState<ReviewGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const work = prefetched
      ? Promise.resolve(buildPrefetched(prefetched))
      : loadGroups(childId, grade);
    work
      .then((result) => {
        if (!cancelled) setGroups(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Could not load the answers.');
      });
    return () => {
      cancelled = true;
    };
  }, [childId, grade, prefetched]);

  return (
    <section className="sl-review" id="sl-review">
      <h2 className="sl-enclosed__title">The answers, one by one</h2>
      <p className="sl-enclosed__sub">
        Exactly what {childName || 'your child'} answered in each activity, and what the answer was.
      </p>

      {error && <p className="sl-rev__notice">{error}</p>}
      {!groups && !error && <p className="sl-rev__notice">Loading the answers…</p>}
      {groups?.map((group) => (
        <Group key={group.key} group={group} forceOpen={expandAll} />
      ))}
    </section>
  );
};

export default AnswerReview;
